import React, { useRef } from 'react';
import * as THREE from 'three';
import { useFrame } from '@react-three/fiber';

const CurrentWire = React.memo(({ conductor }) => {
  const matRef = useRef();
  const geometryRef = useRef();
  const pathRef = useRef();

  if (!pathRef.current) {
    pathRef.current = new THREE.CatmullRomCurve3([
      new THREE.Vector3(
        conductor.start[0],
        conductor.start[1],
        conductor.start[2],
      ),
      new THREE.Vector3(conductor.end[0], conductor.end[1], conductor.end[2]),
    ]);
  }

  if (!geometryRef.current) {
    geometryRef.current = new THREE.TubeGeometry(
      pathRef.current,
      64,
      0.08,
      8,
      false,
    );
  }

  const getShaderColors = (conductor) => {
    if (conductor.label === 'DC+') {
      return {
        baseColor: [0.2, 0.3, 0.8],
        flowColor: [0.8, 0.9, 1.0],
      };
    } else if (conductor.label === 'DC-') {
      return {
        baseColor: [0.1, 0.4, 0.7],
        flowColor: [0.6, 0.8, 1.0],
      };
    } else if (conductor.label === 'AC') {
      return {
        baseColor: [0.0, 0.5, 0.9],
        flowColor: [0.4, 0.8, 1.0],
      };
    } else if (conductor.label === 'Earth') {
      return {
        baseColor: [0.0, 0.6, 0.4],
        flowColor: [0.3, 0.9, 0.7],
      };
    } else {
      return {
        baseColor: [0.3, 0.5, 0.8],
        flowColor: [0.7, 0.9, 1.0],
      };
    }
  };

  const colors = getShaderColors(conductor);

  useFrame((state) => {
    if (
      matRef.current &&
      matRef.current.uniforms &&
      matRef.current.uniforms.uTime
    ) {
      matRef.current.uniforms.uTime.value = state.clock.getElapsedTime();
    }
  });

  return (
    <mesh geometry={geometryRef.current}>
      <shaderMaterial
        ref={matRef}
        vertexShader={`
          varying vec2 vUv;
          void main() {
            vUv = uv;
            gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
          }
        `}
        fragmentShader={`
          uniform float uTime;
          uniform vec3 uBaseColor;
          uniform vec3 uFlowColor;
          varying vec2 vUv;

          void main() {
            float flow1 = sin((vUv.x * 25.0) - (uTime * 6.0));
            float flow2 = sin((vUv.x * 15.0) - (uTime * 4.0) + 1.0);
            float flow3 = sin((vUv.x * 30.0) - (uTime * 8.0) + 2.0);
            float combinedFlow = (flow1 + flow2 + flow3) / 3.0;
            float intensity = smoothstep(0.2, 0.8, combinedFlow);
            intensity = pow(intensity, 0.7);
            float glow = smoothstep(0.0, 1.0, combinedFlow) * 0.3;
            vec3 color = mix(uBaseColor, uFlowColor, intensity);
            color += glow * uFlowColor;
            gl_FragColor = vec4(color, 1.0);
          }
        `}
        uniforms={{
          uTime: { value: 0 },
          uBaseColor: { value: new THREE.Vector3(...colors.baseColor) },
          uFlowColor: { value: new THREE.Vector3(...colors.flowColor) },
        }}
        transparent={false}
        depthWrite={true}
        depthTest={true}
        side={THREE.DoubleSide}
      />
    </mesh>
  );
});

export default CurrentWire;
